// ============================================================================
// Gerüst-Entwurf: Fließtext aus Slots + an/aus-Bausteinen
// Polar CONTRACT_MUSTER_CLEAN_SLOTS: kein Entwurfsmüll, kein ARKORBIT-AGB.
// ============================================================================

import type { EigeneKlausel } from './contractDraftStore.ts';
import { individuellOutputItems } from './contractDraftStore.ts';
import {
  PHILLIP_MUSTER_LABEL,
  gesellschafterLetter,
  resolveGesellschafterListe,
} from './contractGeruest.ts';
import type { ContractTemplateDef, ContractValues } from './contractTemplates.ts';
import { getByPath, isBlockActive } from './contractTemplates.ts';

export const EMPTY_SLOT = '________';

const FORBIDDEN_DRAFT_GARBAGE = [
  { re: /!{3,}/, label: '(!!!!!!)' },
  { re: /X{4,}/i, label: 'XXXX' },
  { re: /zwischen\s+zwischen/i, label: 'zwischen zwischen' },
  { re: /stellt den Arbeitgeber frei/i, label: 'Dienstgeber/Arbeitgeber-Widerspruch' },
  { re: /\(Unterschrift\)/, label: 'Ort/Datum (Unterschrift)' },
  { re: /Unterschrift:\s*_+/i, label: 'Unterschrift-Platzhalterwort' },
];

export function forbiddenDraftGarbageHits(text: string): string[] {
  const t = String(text || '');
  return FORBIDDEN_DRAFT_GARBAGE.filter((g) => g.re.test(t)).map((g) => g.label);
}

function filled(form: ContractValues, path: string): boolean {
  const v = getByPath(form, path);
  if (typeof v === 'boolean') return true;
  if (typeof v === 'number') return Number.isFinite(v);
  if (Array.isArray(v)) return v.some((x) => String(x || '').trim());
  return typeof v === 'string' && v.trim().length > 0;
}

function slot(form: ContractValues, path: string): string {
  const v = getByPath(form, path);
  if (typeof v === 'boolean') return v ? 'ja' : 'nein';
  if (typeof v === 'number' && Number.isFinite(v)) {
    return Number.isInteger(v) ? String(v) : v.toLocaleString('de-AT');
  }
  if (Array.isArray(v)) {
    const parts = v.map((x) => String(x || '').trim()).filter(Boolean);
    return parts.length ? parts.join(', ') : EMPTY_SLOT;
  }
  const s = typeof v === 'string' ? v.trim() : '';
  if (!s) return EMPTY_SLOT;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
  return s;
}

function eur(form: ContractValues, path: string): string {
  const v = getByPath(form, path);
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v.toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  const s = typeof v === 'string' ? v.trim() : '';
  if (!s) return EMPTY_SLOT;
  return s;
}

function joinSections(parts: Array<string | null | undefined>): string {
  return parts
    .map((p) => (typeof p === 'string' ? p.trim() : ''))
    .filter(Boolean)
    .join('\n\n');
}

function banner(template: ContractTemplateDef): string {
  const label = template.sourceMuster?.label || PHILLIP_MUSTER_LABEL;
  return [
    template.schema.title.replace(/\s+—\s+Gerüst$/, ''),
    `${label}`,
    'Kein ARKORBIT-AGB. Entwurfs-Assistent, keine Rechtsberatung (§ 9 RAO).',
  ].join('\n');
}

function eigeneBlock(eigene: EigeneKlausel[]): string | null {
  const items = individuellOutputItems(eigene);
  if (!items.length) return null;
  return items
    .map((k) => `${k.titel}\n${k.text || EMPTY_SLOT}`)
    .join('\n\n');
}

function partyLines(
  form: ContractValues,
  prefix: string,
  role: string,
  extra: Array<{ path: string; label: string }>,
): string {
  const lines = [`${role}`, slot(form, `${prefix}.name`)];
  for (const e of extra) {
    if (filled(form, `${prefix}.${e.path}`)) lines.push(`${e.label}: ${slot(form, `${prefix}.${e.path}`)}`);
  }
  if (filled(form, `${prefix}.anschrift`)) lines.push(`Anschrift: ${slot(form, `${prefix}.anschrift`)}`);
  else lines.push(`Anschrift: ${EMPTY_SLOT}`);
  lines.push(`(im Folgenden der/die „${role}“)`);
  return lines.join('\n');
}

type Ctx = {
  form: ContractValues;
  extra: Record<string, unknown>;
  on: (id: string) => boolean;
};

function extraSlot(extra: Record<string, unknown>, id: string): string {
  const v = extra[id];
  if (typeof v === 'boolean') return v ? 'ja' : 'nein';
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  const s = typeof v === 'string' ? v.trim() : '';
  return s || EMPTY_SLOT;
}

function extraFilled(extra: Record<string, unknown>, id: string): boolean {
  const v = extra[id];
  if (typeof v === 'boolean') return true;
  if (typeof v === 'number') return Number.isFinite(v);
  return typeof v === 'string' && v.trim().length > 0;
}

function buildArbeit(ctx: Ctx): string {
  const { form, extra, on } = ctx;
  const parts: Array<string | null> = [];

  if (on('parteien')) {
    parts.push(
      joinSections([
        'Abgeschlossen zwischen',
        partyLines(form, 'dienstgeber', 'Dienstgeber', [{ path: 'fn', label: 'FN' }]),
        'und',
        partyLines(form, 'dienstnehmer', 'Dienstnehmer', [
          { path: 'geburtsdatum', label: 'Geburtsdatum' },
          { path: 'svnr', label: 'Sozialversicherungsnummer' },
        ]),
        'wird folgender Arbeitsvertrag geschlossen.',
      ]),
    );
  }

  if (on('vertragsabschluss')) {
    parts.push(
      [
        '§ 1 Vertragsabschluss',
        'Der Dienstgeber stellt den Dienstnehmer als Arbeitnehmer im Sinne der österreichischen arbeitsrechtlichen Vorschriften an. Der Dienstnehmer nimmt diese Anstellung unter den in diesem Vertrag dargelegten Rahmenbedingungen an.',
        'Mit Unterfertigung dieses Vertrages kommt zwischen den Vertragsparteien ein Arbeitsverhältnis zustande.',
      ].join('\n'),
    );
  }

  if (on('beginn')) {
    const befristet = getByPath(form, 'befristet') === true;
    parts.push(
      [
        '§ 2 Beginn des Arbeitsverhältnisses',
        `Das Arbeitsverhältnis beginnt am ${slot(form, 'beginn')}.`,
        befristet
          ? `Das Arbeitsverhältnis wird befristet bis zum ${slot(form, 'befristung_bis')} abgeschlossen und endet an diesem Tag, ohne dass es einer gesonderten Kündigung durch eine der Vertragsparteien bedarf.`
          : 'Es wird auf unbestimmte Zeit abgeschlossen.',
      ].join('\n'),
    );
  }

  if (on('taetigkeit')) {
    const aufgaben = filled(form, 'aufgaben')
      ? slot(form, 'aufgaben')
      : EMPTY_SLOT;
    parts.push(
      [
        '§ 3 Tätigkeit',
        `Der Dienstnehmer wird als ${slot(form, 'position')} beschäftigt.`,
        'Zu den Aufgaben gehören insbesondere:',
        aufgaben,
        'Der Dienstgeber ist berechtigt, dem Dienstnehmer andere gleichwertige und zumutbare Tätigkeiten zuzuweisen, soweit betriebliche Erfordernisse dies notwendig machen.',
      ].join('\n'),
    );
  }

  if (on('dienstort')) {
    parts.push(
      [
        '§ 4 Dienstort',
        `Dienstort ist grundsätzlich: ${slot(form, 'dienstort')}.`,
        'Der Dienstnehmer erklärt sich bereit, vorübergehend auch an anderen Arbeitsorten des Dienstgebers tätig zu werden, soweit dies betrieblich erforderlich und zumutbar ist.',
      ].join('\n'),
    );
  }

  if (on('arbeitszeit')) {
    parts.push(
      [
        '§ 5 Arbeitszeit',
        `Die regelmäßige Normalarbeitszeit beträgt ${slot(form, 'wochenstunden')} Stunden pro Woche.`,
        'Die konkrete Lage der Arbeitszeit richtet sich nach dem Arbeitszeitgesetz (AZG), betrieblichen Erfordernissen sowie allfälligen Arbeitszeitregelungen des Unternehmens.',
      ].join('\n'),
    );
  }

  if (on('ueberstunden')) {
    parts.push(
      [
        'Überstunden sind nur auf Anordnung oder mit Zustimmung des Dienstgebers zu leisten.',
        'Überstunden sind im Rahmen der Aufgabenerfüllung zu leisten und zu dokumentieren. Die Dokumentation ist dem Dienstgeber vor Auszahlung, spätestens zum Letzten jedes Monats, zur Gegenzeichnung und Prüfung vorzulegen.',
      ].join('\n'),
    );
  }

  if (on('entgelt')) {
    const zahlung = filled(form, 'zahlungstermin')
      ? `zahlbar jeweils ${slot(form, 'zahlungstermin')}`
      : `zahlbar jeweils ${EMPTY_SLOT}`;
    const iban = filled(form, 'iban') ? ` auf das Konto IBAN ${slot(form, 'iban')}` : '';
    parts.push(
      [
        '§ 6 Entgelt',
        `Der Dienstnehmer erhält ein monatliches Bruttogehalt von EUR ${eur(form, 'brutto_eur')}, ${zahlung}${iban}.`,
        'Mit dem Entgelt sind nur jene Leistungen abgegolten, die ausdrücklich durch Gesetz, Kollektivvertrag oder diesen Vertrag als abgegolten bezeichnet werden.',
        'Soweit gesetzlich oder kollektivvertraglich vorgesehen, gebühren gesetzliche Sonderzahlungen (Urlaubs- und Weihnachtsremuneration).',
      ].join('\n'),
    );
  }

  if (on('kollektivvertrag')) {
    parts.push(
      [
        '§ 7 Anwendbarer Kollektivvertrag',
        'Auf das Arbeitsverhältnis findet folgender Kollektivvertrag Anwendung:',
        slot(form, 'kollektivvertrag'),
        'in der jeweils geltenden Fassung.',
        'Soweit dieser Vertrag keine Regelung enthält, gelten die Bestimmungen des anwendbaren Kollektivvertrages sowie die einschlägigen gesetzlichen Vorschriften.',
      ].join('\n'),
    );
  }

  if (on('ueberzahlung')) {
    parts.push(
      [
        'Überzahlung',
        `Kollektivvertrag Vollzeit brutto: EUR ${eur(form, 'kv_vollzeit_brutto')}`,
        `Überzahlung: EUR ${eur(form, 'ueberzahlung_eur')}`,
        `Entgelt pro Monat: EUR ${eur(form, 'brutto_eur')}`,
      ].join('\n'),
    );
  }

  if (on('probezeit')) {
    parts.push(
      [
        '§ 8 Probezeit',
        `Die ersten ${slot(form, 'probezeit_dauer')} gelten als Probezeit.`,
        'Während der Probezeit kann das Arbeitsverhältnis von beiden Vertragsparteien jederzeit ohne Angabe von Gründen und ohne Einhaltung von Fristen aufgelöst werden.',
      ].join('\n'),
    );
  }

  if (on('urlaub')) {
    parts.push(
      [
        '§ 9 Urlaub',
        'Der Dienstnehmer hat Anspruch auf Urlaub nach den Bestimmungen des Urlaubsgesetzes (UrlG).',
        'Die zeitliche Inanspruchnahme des Urlaubs erfolgt im Einvernehmen zwischen Dienstgeber und Dienstnehmer unter Berücksichtigung der betrieblichen Interessen.',
        'Der Wille, Urlaub zu bestimmter Zeit antreten zu wollen, ist dem Dienstgeber in zumutbarer Zeit anzukündigen.',
      ].join('\n'),
    );
  }

  if (on('krankheit')) {
    parts.push(
      [
        '§ 10 Entgeltfortzahlung im Krankheitsfall',
        'Im Falle einer krankheitsbedingten Dienstverhinderung gelten die gesetzlichen Bestimmungen, insbesondere das Angestelltengesetz bzw. Entgeltfortzahlungsgesetz.',
        'Der Dienstnehmer hat jede Dienstverhinderung unverzüglich bekannt zu geben und auf Verlangen eine ärztliche Bestätigung vorzulegen.',
        'Der Dienstnehmer hat auch ohne ausdrückliches Verlangen ehestmöglich eine ärztliche Bestätigung vorzulegen, wenn die Dauer des Fernbleibens vom Dienstort drei Arbeitstage überschreitet oder ein solches Überschreiten stark anzunehmen ist.',
      ].join('\n'),
    );
  }

  if (on('verschwiegenheit')) {
    parts.push(
      [
        '§ 11 Verschwiegenheitspflicht',
        'Der Dienstnehmer verpflichtet sich, über sämtliche Geschäfts- und Betriebsgeheimnisse sowie sonstige vertrauliche Informationen des Dienstgebers Stillschweigen zu bewahren.',
        'Ebenso verpflichtet er sich, erhaltene Materialien und Informationsträger sicher zu verwahren und in angemessenem Maße zu ihrer Sicherheit Sorge zu tragen.',
        'Diese Verpflichtung gilt auch nach Beendigung des Arbeitsverhältnisses fort.',
      ].join('\n'),
    );
  }

  if (on('datenschutz')) {
    parts.push(
      [
        '§ 12 Datenschutz',
        'Der Dienstnehmer nimmt zur Kenntnis, dass personenbezogene Daten im Rahmen der gesetzlichen Vorschriften verarbeitet werden, soweit dies für die Durchführung des Arbeitsverhältnisses erforderlich ist.',
      ].join('\n'),
    );
  }

  if (on('nebentatigkeit')) {
    parts.push(
      [
        '§ 13 Nebentätigkeiten',
        'Jede entgeltliche Nebentätigkeit ist dem Dienstgeber vor Aufnahme schriftlich bekannt zu geben.',
        'Eine Nebentätigkeit darf berechtigte betriebliche Interessen des Dienstgebers nicht beeinträchtigen.',
      ].join('\n'),
    );
  }

  if (on('beendigung')) {
    parts.push(
      [
        '§ 14 Beendigung des Arbeitsverhältnisses',
        'Nach Ablauf einer allenfalls vereinbarten Probezeit kann das Arbeitsverhältnis unter Einhaltung der gesetzlichen, kollektivvertraglichen oder vertraglich vereinbarten Kündigungsfristen und Kündigungstermine beendet werden.',
        'Das Recht zur vorzeitigen Auflösung aus wichtigem Grund bleibt unberührt.',
      ].join('\n'),
    );
  }

  if (on('kuendigungsfrist')) {
    const monate = extraFilled(extra, 'kuendigungsfrist_monate')
      ? extraSlot(extra, 'kuendigungsfrist_monate')
      : filled(form, 'kuendigungsfrist_monate')
        ? slot(form, 'kuendigungsfrist_monate')
        : EMPTY_SLOT;
    parts.push(
      `Die Kündigungsfrist beträgt ${monate} Monate. Sie ist schriftlich zuzustellen und kann zur Mitte des Monats sowie am Monatsletzten erfolgen.`,
    );
  }

  if (on('freistellung')) {
    parts.push(
      'Der Dienstgeber behält sich vor, den Dienstnehmer nach Aussprache einer Kündigung mit sofortiger Wirkung bis auf Widerruf vom Dienst freizustellen.',
    );
  }

  if (on('rueckgabe')) {
    parts.push(
      [
        '§ 15 Rückgabe von Betriebseigentum',
        'Spätestens mit Beendigung des Arbeitsverhältnisses hat der Dienstnehmer sämtliche ihm überlassenen Unterlagen, Datenträger, Schlüssel, Geräte sowie sonstiges Eigentum des Dienstgebers unverzüglich zurückzugeben.',
      ].join('\n'),
    );
  }

  if (on('schluss')) {
    parts.push(
      [
        '§ 16 Schriftform',
        'Änderungen und Ergänzungen dieses Vertrages bedürfen zu ihrer Gültigkeit der Schriftform, soweit gesetzlich zulässig.',
        '',
        '§ 17 Schlussbestimmungen',
        'Sollten einzelne Bestimmungen dieses Vertrages ganz oder teilweise unwirksam sein oder werden, berührt dies die Gültigkeit der übrigen Bestimmungen nicht.',
        'Auf diesen Vertrag ist österreichisches Recht anzuwenden.',
        '',
        '§ 18 Dienstzettel und Informationen gemäß AVRAG',
        'Der Dienstnehmer bestätigt, die gesetzlich erforderlichen Informationen gemäß Arbeitsvertragsrechts-Anpassungsgesetz (AVRAG) erhalten zu haben bzw. mit Unterfertigung dieses Vertrages zu erhalten.',
        'Dieser Vertrag wird in zwei gleichlautenden Ausfertigungen errichtet. Jede Vertragspartei erhält eine Ausfertigung.',
        '',
        `Ort, Datum: ${slot(form, 'ort_datum')}`,
        'Für den Dienstgeber',
        `Name: ${slot(form, 'dienstgeber.name')}`,
        EMPTY_SLOT,
        'Der Dienstnehmer',
        `Name: ${slot(form, 'dienstnehmer.name')}`,
        EMPTY_SLOT,
      ].join('\n'),
    );
  }

  return joinSections(parts);
}

function buildKauf(ctx: Ctx): string {
  const { form, on } = ctx;
  const liegenschaft = String(getByPath(form, 'gegenstand_art') || '') === 'Liegenschaft';
  const parts: Array<string | null> = [];

  if (on('parteien')) {
    parts.push(
      joinSections([
        'Abgeschlossen zwischen',
        partyLines(form, 'verkaeufer', 'Verkäufer', [
          { path: 'geburtsdatum_oder_fn', label: 'Geburtsdatum / FN' },
        ]),
        'und',
        partyLines(form, 'kaeufer', 'Käufer', [
          { path: 'geburtsdatum_oder_fn', label: 'Geburtsdatum / FN' },
        ]),
        'gemeinsam auch „Vertragsparteien“ genannt.',
      ]),
    );
  }

  if (on('gegenstand')) {
    const idLine = filled(form, 'gegenstand.identifikation')
      ? `Identifikation (z. B. Seriennummer): ${slot(form, 'gegenstand.identifikation')}`
      : null;
    const lage = liegenschaft
      ? `Lage / EZ: ${slot(form, 'gegenstand.lage_oder_beschreibung')}`
      : filled(form, 'gegenstand.lage_oder_beschreibung')
        ? `Beschreibung: ${slot(form, 'gegenstand.lage_oder_beschreibung')}`
        : filled(form, 'gegenstand.beschreibung')
          ? `Beschreibung: ${slot(form, 'gegenstand.beschreibung')}`
          : `Beschreibung: ${EMPTY_SLOT}`;
    const zustand = filled(form, 'gegenstand.zustand')
      ? `Zustand: ${slot(form, 'gegenstand.zustand')}`
      : null;
    parts.push(
      joinSections([
        [
          '§ 1 Vertragsgegenstand',
          `Kaufgegenstand (${slot(form, 'gegenstand_art')}): ${slot(form, 'gegenstand.bezeichnung')}`,
          lage,
        ].join('\n'),
        idLine,
        zustand,
        'Der Käufer kauft den vorbezeichneten Kaufgegenstand zu den Bedingungen dieses Vertrages.',
      ]),
    );
  }

  if (on('preis_uebergabe') || on('kaufpreis')) {
    const art = String(getByPath(form, 'zahlungsart') || '').trim();
    const zahlung =
      art === 'bei Vertragsunterzeichnung'
        ? 'Der Kaufpreis ist bei Vertragsunterzeichnung zu bezahlen.'
        : art === 'bei Übergabe'
          ? 'Der Kaufpreis ist bei Übergabe zu bezahlen.'
          : art === 'bis Stichtag'
            ? `Der Kaufpreis ist bis spätestens ${slot(form, 'zahlung_bis')} zu bezahlen.`
            : art === 'Überweisung'
              ? `Der Kaufpreis ist mittels Überweisung auf IBAN ${slot(form, 'iban')} zu bezahlen.`
              : `Der Kaufpreis ist zu bezahlen: ${art || EMPTY_SLOT}.`;
    const worten = filled(form, 'kaufpreis_worten')
      ? `(in Worten: ${slot(form, 'kaufpreis_worten')})`
      : null;
    parts.push(
      joinSections([
        ['§ 2 Kaufpreis', `Der Kaufpreis beträgt EUR ${eur(form, 'kaufpreis_eur')}.`, worten, zahlung].join('\n'),
      ]),
    );
  }

  if (on('uebergabe')) {
    parts.push(
      [
        '§ 3 Übergabe und Übernahme',
        `Die Übergabe des Kaufgegenstandes erfolgt am ${slot(form, 'uebergabe_am')} am Ort ${slot(form, 'uebergabe_ort')}.`,
        'Mit der Übergabe geht die Gefahr des zufälligen Untergangs und der zufälligen Verschlechterung auf den Käufer über.',
        'Der Käufer bestätigt nach erfolgter Übergabe den ordnungsgemäßen Empfang des Kaufgegenstandes.',
      ].join('\n'),
    );
  }

  if (on('eigentum')) {
    parts.push(
      [
        '§ 4 Eigentumsübergang',
        'Das Eigentum am Kaufgegenstand geht mit vollständiger Bezahlung des Kaufpreises und Übergabe auf den Käufer über.',
        'Bis zur vollständigen Kaufpreiszahlung bleibt der Kaufgegenstand im Eigentum des Verkäufers (Eigentumsvorbehalt).',
      ].join('\n'),
    );
  }

  if (on('gewaehrleistung')) {
    const variante = String(getByPath(form, 'gewaehrleistung_variante') || '').trim();
    const body =
      variante === 'Privatverkauf'
        ? [
            'Bei einem Kauf zwischen Privatpersonen wird die Gewährleistung, soweit gesetzlich zulässig, ausgeschlossen.',
            'Der Ausschluss gilt nicht für arglistig verschwiegene Mängel, ausdrücklich zugesicherte Eigenschaften und gesetzlich nicht ausschließbare Ansprüche.',
          ].join('\n')
        : variante === 'Unternehmergeschäft'
          ? 'Für Unternehmer gelten die gesetzlichen Gewährleistungsbestimmungen, soweit nicht ausdrücklich etwas anderes vereinbart wird.'
          : `Gewährleistungsvariante: ${EMPTY_SLOT}`;
    parts.push(['§ 5 Gewährleistung', body].join('\n'));
  }

  if (on('zusicherungen')) {
    parts.push(
      [
        '§ 6 Zusicherungen des Verkäufers',
        'Der Verkäufer versichert, dass er Eigentümer des Kaufgegenstandes ist; der Kaufgegenstand frei von Rechten Dritter ist, soweit nicht ausdrücklich anders angegeben; und keine ihm bekannten Rechtsmängel bestehen.',
      ].join('\n'),
    );
  }

  if (on('untersuchung')) {
    parts.push(
      [
        '§ 7 Untersuchungspflicht',
        'Der Käufer hat den Kaufgegenstand bei Übergabe zu prüfen und erkennbare Mängel unverzüglich anzuzeigen.',
        'Für Unternehmer gelten zusätzlich die Bestimmungen des Unternehmensgesetzbuches (UGB) über die Pflicht zur Mängelrüge.',
      ].join('\n'),
    );
  }

  if (on('aufloesung')) {
    parts.push(
      [
        '§ 8 Vertragsauflösung',
        'Bei wesentlicher Vertragsverletzung kann die jeweils andere Vertragspartei nach Setzung einer angemessenen Nachfrist vom Vertrag zurücktreten.',
        'Weitergehende gesetzliche Ansprüche bleiben unberührt.',
      ].join('\n'),
    );
  }

  if (on('kosten')) {
    parts.push(
      [
        '§ 9 Kosten und Abgaben',
        `Die mit diesem Vertrag verbundenen Kosten, Gebühren und Abgaben trägt: ${slot(form, 'kosten_tragung')}.`,
      ].join('\n'),
    );
  }

  if (on('datenschutz')) {
    parts.push(
      [
        '§ 10 Datenschutz und Vertraulichkeit',
        'Die Vertragsparteien verpflichten sich zu einem vertraulichen Umgang mit den im Rahmen dieses Vertrages bekannt gewordenen personenbezogenen Daten und Informationen.',
      ].join('\n'),
    );
  }

  if (on('schluss')) {
    parts.push(
      [
        '§ 11 Schriftform',
        'Änderungen und Ergänzungen dieses Vertrages bedürfen der Schriftform. Mündliche Nebenabreden bestehen nicht.',
        '',
        '§ 12 Salvatorische Klausel',
        'Sollten einzelne Bestimmungen dieses Vertrages ganz oder teilweise unwirksam oder undurchführbar sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.',
        '',
        '§ 13 Anwendbares Recht und Gerichtsstand',
        'Auf diesen Vertrag ist ausschließlich österreichisches Recht anzuwenden.',
        'Für Streitigkeiten aus diesem Vertrag ist, soweit gesetzlich zulässig, das sachlich zuständige Gericht am Wohn- bzw. Unternehmenssitz des Verkäufers zuständig.',
        '',
        '§ 14 Schlussbestimmungen',
        'Dieser Vertrag wird in zwei gleichlautenden Ausfertigungen errichtet. Jede Vertragspartei erhält eine Ausfertigung.',
        `Ort: ${slot(form, 'ort')}  Datum: ${slot(form, 'datum')}`,
        `Verkäufer: ${slot(form, 'verkaeufer.name')}`,
        EMPTY_SLOT,
        `Käufer: ${slot(form, 'kaeufer.name')}`,
        EMPTY_SLOT,
      ].join('\n'),
    );
  }

  return joinSections(parts);
}

function buildPfand(ctx: Ctx): string {
  const { form, on } = ctx;
  const isLeihe = String(getByPath(form, 'vertragstyp') || '') === 'Leihvertrag';
  if (isLeihe) {
    return joinSections([
      'Leihvertrag — für diesen Untertyp liegt keine ARKO-Vorlage vor. Nur Slots, keine erfundenen Klauseln.',
      `Geber: ${slot(form, 'geber.name')}`,
      `Nehmer: ${slot(form, 'nehmer.name')}`,
      `Gegenstand: ${slot(form, 'gegenstand')}`,
      `Beginn: ${slot(form, 'beginn')}`,
      getByPath(form, 'befristet') === true ? `Ende: ${slot(form, 'ende')}` : 'Unbefristet.',
    ]);
  }

  const parts: Array<string | null> = [];
  if (on('typ_parteien') || on('parteien')) {
    parts.push(
      joinSections([
        'Abgeschlossen zwischen',
        partyLines(form, 'geber', 'Pfandgläubiger', [
          { path: 'geburtsdatum_oder_fn', label: 'Geburtsdatum / FN' },
        ]),
        'und',
        partyLines(form, 'nehmer', 'Pfandgeber', [
          { path: 'geburtsdatum_oder_fn', label: 'Geburtsdatum / FN' },
        ]),
        'gemeinsam „Vertragsparteien“ genannt.',
      ]),
    );
  }

  if (on('praeambel')) {
    parts.push(
      [
        '§ 1 Präambel',
        'Der Pfandgeber ist Eigentümer des nachstehend bezeichneten Pfandgegenstandes und bestellt zur Sicherung der in diesem Vertrag bezeichneten Forderung ein Pfandrecht zugunsten des Pfandgläubigers.',
      ].join('\n'),
    );
  }

  if (on('forderung')) {
    parts.push(
      [
        '§ 2 Gesicherte Forderung',
        `Rechtsgrund: ${slot(form, 'rechtsgrund')}`,
        `Schuldner: ${slot(form, 'schuldner')}`,
        `Forderungshöhe: EUR ${eur(form, 'forderung_eur')}`,
        'sowie allfälliger Zinsen, Verzugszinsen, Kosten der Rechtsverfolgung und Nebenforderungen.',
      ].join('\n'),
    );
  }

  if (on('gegenstand')) {
    parts.push(
      [
        '§ 3 Pfandgegenstand',
        `Bezeichnung: ${slot(form, 'gegenstand')}`,
        filled(form, 'gegenstand_beschreibung')
          ? `Beschreibung: ${slot(form, 'gegenstand_beschreibung')}`
          : null,
        filled(form, 'identifikation') ? `Identifikation: ${slot(form, 'identifikation')}` : null,
        filled(form, 'schaetzwert_eur') ? `Geschätzter Wert: EUR ${eur(form, 'schaetzwert_eur')}` : null,
        'Der Pfandgeber erklärt, uneingeschränkter Eigentümer des Pfandgegenstandes zu sein.',
      ]
        .filter(Boolean)
        .join('\n'),
    );
  }

  if (on('bestellung')) {
    parts.push(
      [
        '§ 4 Bestellung des Pfandrechts',
        'Der Pfandgeber bestellt zugunsten des Pfandgläubigers ein Pfandrecht am Pfandgegenstand zur Sicherung der in § 2 genannten Forderung. Der Pfandgläubiger nimmt die Pfandbestellung an.',
      ].join('\n'),
    );
  }

  if (on('uebergabe') || on('dauer')) {
    parts.push(
      [
        '§ 5 Übergabe des Pfandgegenstandes',
        `Der Pfandgegenstand wird dem Pfandgläubiger am ${slot(form, 'beginn')} übergeben.`,
        'Mit der tatsächlichen Übergabe wird das Pfandrecht wirksam begründet.',
      ].join('\n'),
    );
  }

  if (on('sicherheit') || on('verwahrung')) {
    parts.push(
      [
        '§ 6 Verwahrung',
        'Der Pfandgläubiger verpflichtet sich, den Pfandgegenstand mit der Sorgfalt eines ordentlichen Verwahrers aufzubewahren.',
        'Eine Benutzung des Pfandgegenstandes ist ohne ausdrückliche Zustimmung des Pfandgebers unzulässig.',
      ].join('\n'),
    );
  }

  if (on('erhaltung')) {
    parts.push(
      [
        '§ 7 Erhaltungspflichten',
        'Der Pfandgeber hat den Pfandgläubiger über alle Umstände zu informieren, die den Wert des Pfandgegenstandes beeinträchtigen könnten.',
        'Der Pfandgläubiger ist verpflichtet, den Pfandgegenstand vor Beschädigung oder Verlust angemessen zu schützen.',
      ].join('\n'),
    );
  }

  if (on('verwertung')) {
    parts.push(
      [
        '§ 8 Verwertungsrecht',
        'Kommt der Schuldner mit der Erfüllung der gesicherten Forderung in Verzug, ist der Pfandgläubiger berechtigt, das Pfand nach den gesetzlichen Bestimmungen zu verwerten.',
        'Der Erlös ist auf die gesicherte Forderung anzurechnen. Ein allfälliger Überschuss ist an den Pfandgeber auszuzahlen.',
      ].join('\n'),
    );
  }

  if (on('rueckgabe')) {
    parts.push(
      [
        '§ 9 Rückgabe des Pfandgegenstandes',
        'Nach vollständiger Tilgung der gesicherten Forderung einschließlich aller Nebengebühren hat der Pfandgläubiger den Pfandgegenstand unverzüglich an den Pfandgeber zurückzugeben.',
      ].join('\n'),
    );
  }

  if (on('gewaehrleistungen')) {
    parts.push(
      [
        '§ 10 Gewährleistungen des Pfandgebers',
        'Der Pfandgeber gewährleistet, dass er alleiniger Eigentümer des Pfandgegenstandes ist; der Pfandgegenstand frei von Rechten Dritter ist, sofern nichts anderes offengelegt wurde; und keine Umstände bekannt sind, welche die Verwertbarkeit wesentlich einschränken.',
      ].join('\n'),
    );
  }

  if (on('haftung')) {
    parts.push(
      [
        '§ 11 Haftung',
        'Der Pfandgläubiger haftet für Vorsatz und grobe Fahrlässigkeit nach den gesetzlichen Bestimmungen.',
        'Eine weitergehende Haftung kann, soweit gesetzlich zulässig, ausgeschlossen werden.',
      ].join('\n'),
    );
  }

  if (on('kosten')) {
    parts.push(
      ['§ 12 Kosten', `Die Kosten trägt: ${slot(form, 'kosten_tragung')}.`].join('\n'),
    );
  }

  if (on('schluss')) {
    parts.push(
      [
        '§ 13 Vertragsdauer',
        'Dieser Vertrag endet mit dem Erlöschen der gesicherten Forderung und der Rückgabe des Pfandgegenstandes.',
        '',
        '§ 14 Schriftform',
        'Änderungen und Ergänzungen dieses Vertrages bedürfen der Schriftform. Mündliche Nebenabreden bestehen nicht.',
        '',
        '§ 15 Salvatorische Klausel',
        'Sollten einzelne Bestimmungen dieses Vertrages unwirksam oder undurchführbar sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.',
        '',
        '§ 16 Anwendbares Recht und Gerichtsstand',
        'Auf diesen Vertrag ist ausschließlich österreichisches Recht anzuwenden.',
        'Für Streitigkeiten aus diesem Vertrag wird, soweit gesetzlich zulässig, die Zuständigkeit des sachlich zuständigen Gerichts am Wohn- bzw. Unternehmenssitz des Pfandgläubigers vereinbart.',
        '',
        'Dieser Vertrag wird in zwei gleichlautenden Ausfertigungen errichtet.',
        `Ort: ${slot(form, 'ort')}  Datum: ${slot(form, 'datum')}`,
        `Pfandgläubiger: ${slot(form, 'geber.name')}`,
        EMPTY_SLOT,
        `Pfandgeber: ${slot(form, 'nehmer.name')}`,
        EMPTY_SLOT,
      ].join('\n'),
    );
  }

  return joinSections(parts);
}

function buildDarlehen(ctx: Ctx): string {
  const { form, on } = ctx;
  const parts: Array<string | null> = [];
  const waehrung = filled(form, 'waehrung') ? slot(form, 'waehrung') : 'EUR';

  if (on('parteien')) {
    parts.push(
      joinSections([
        'Abgeschlossen zwischen',
        partyLines(form, 'darlehensgeber', 'Darlehensgeber', [
          { path: 'geburtsdatum_oder_fn', label: 'Geburtsdatum / FN' },
        ]),
        'und',
        partyLines(form, 'darlehensnehmer', 'Darlehensnehmer', [
          { path: 'geburtsdatum_oder_fn', label: 'Geburtsdatum / FN' },
        ]),
        'gemeinsam auch „Vertragsparteien“ genannt.',
      ]),
    );
  }

  if (on('betrag_laufzeit') || on('gegenstand')) {
    const worten = filled(form, 'betrag_worten')
      ? `(in Worten: ${slot(form, 'betrag_worten')})`
      : `(in Worten: ${EMPTY_SLOT})`;
    parts.push(
      [
        '§ 1 Vertragsgegenstand',
        `Der Darlehensgeber gewährt dem Darlehensnehmer ein Darlehen in Höhe von ${waehrung} ${eur(form, 'betrag_eur')} ${worten}.`,
        'Der Darlehensnehmer nimmt dieses Darlehen unter den in diesem Vertrag dargelegten sowie den gesetzlichen Rahmenbedingungen an.',
      ].join('\n'),
    );
  }

  if (on('auszahlung')) {
    const art = String(getByPath(form, 'auszahlungsart') || '').trim();
    const how =
      art === 'Barauszahlung'
        ? 'Barauszahlung gegen gesonderte Empfangsbestätigung'
        : `Banküberweisung auf IBAN ${slot(form, 'iban')}`;
    parts.push(
      [
        '§ 2 Auszahlung',
        `Das Darlehen wird bis spätestens ${slot(form, 'auszahlung_bis')} ausgezahlt durch ${how}.`,
        'Mit Gutschrift auf dem Konto bzw. Übergabe des Geldbetrages gilt das Darlehen als ausbezahlt.',
      ].join('\n'),
    );
  }

  if (on('betrag_laufzeit') || on('laufzeit')) {
    parts.push(
      [
        '§ 3 Laufzeit',
        `Das Darlehen wird für die Zeit vom ${slot(form, 'laufzeit_von')} bis zum ${slot(form, 'laufzeit_bis')} gewährt.`,
        'Der Darlehensnehmer verpflichtet sich, den geschuldeten Betrag spätestens zum Ende der Laufzeit zurückzuzahlen.',
      ].join('\n'),
    );
  }

  if (on('zins_rueck') || on('verzinsung')) {
    const art = String(getByPath(form, 'verzinsung') || '').trim();
    const zins =
      art === 'unverzinslich'
        ? 'Das Darlehen wird unverzinslich gewährt.'
        : art === 'Zinssatz % p.a.'
          ? `Das Darlehen wird mit einem jährlichen Zinssatz von ${slot(form, 'zinssatz_prozent')} % p.a. verzinst.`
          : art === 'Index / Inflation'
            ? `Das Darlehen wird index- bzw. inflationsbezogen verzinst: ${filled(form, 'index_notiz') ? slot(form, 'index_notiz') : EMPTY_SLOT}.`
            : `Verzinsung: ${art || EMPTY_SLOT}.`;
    const faellig = filled(form, 'zins_faelligkeit')
      ? `Die Zinsen werden ${slot(form, 'zins_faelligkeit')} zur Zahlung fällig.`
      : null;
    parts.push(joinSections([['§ 4 Verzinsung', zins].join('\n'), faellig]));
  }

  if (on('zins_rueck') || on('rueckzahlung')) {
    const art = String(getByPath(form, 'rueckzahlung') || '').trim();
    const body =
      art === 'Raten'
        ? [
            'Die Rückzahlung erfolgt in Raten.',
            `Anzahl der Raten: ${slot(form, 'raten_anzahl')}`,
            `Höhe je Rate: ${waehrung} ${eur(form, 'rate_eur')}`,
            `Fälligkeit jeweils am ${slot(form, 'rate_faelligkeit')} eines Monats.`,
          ].join('\n')
        : art === 'Einmalzahlung'
          ? 'Die Rückzahlung erfolgt als Einmalzahlung zum Fälligkeitstag.'
          : `Rückzahlung: ${art || EMPTY_SLOT}.`;
    const konto = filled(form, 'rueck_iban')
      ? `Zahlungen erfolgen auf IBAN ${slot(form, 'rueck_iban')}.`
      : null;
    parts.push(joinSections([['§ 5 Rückzahlung', body].join('\n'), konto]));
  }

  if (on('vorzeitig')) {
    parts.push(
      [
        '§ 6 Vorzeitige Rückzahlung',
        'Der Darlehensnehmer ist berechtigt, das Darlehen jederzeit ganz oder teilweise vorzeitig zurückzuzahlen.',
        'Im Falle einer unverzinslichen vorzeitigen Rückzahlung entstehen keine zusätzlichen Kosten.',
      ].join('\n'),
    );
  }

  if (on('verzug')) {
    parts.push(
      [
        '§ 7 Verzug',
        'Kommt der Darlehensnehmer mit einer Zahlung in Verzug, so werden gesetzliche Verzugszinsen nach österreichischem Recht fällig.',
        'Darüber hinaus hat der Darlehensnehmer dem Darlehensgeber sämtliche durch den Verzug verursachten notwendigen und zweckmäßigen Kosten zu ersetzen, soweit dies gesetzlich zulässig ist.',
      ].join('\n'),
    );
  }

  if (on('sicherheit')) {
    parts.push(
      [
        '§ 8 Sicherheiten',
        `Zur Besicherung des Darlehens werden folgende Sicherheiten bestellt: ${slot(form, 'sicherheit_art')}.`,
        filled(form, 'sicherheit_notiz') ? slot(form, 'sicherheit_notiz') : null,
        'Die näheren Bedingungen werden in einer gesonderten Vereinbarung geregelt.',
      ]
        .filter(Boolean)
        .join('\n'),
    );
  }

  if (on('kuendigung')) {
    parts.push(
      [
        '§ 9 Kündigung',
        'Der Darlehensgeber kann das Darlehen aus wichtigem Grund mit sofortiger Wirkung kündigen.',
        'Ein wichtiger Grund liegt insbesondere vor, wenn der Darlehensnehmer wesentliche Vertragsverletzungen begeht, Zahlungsverzug trotz Mahnung besteht, über das Vermögen des Darlehensnehmers ein Insolvenzverfahren eröffnet wird oder dessen Eröffnung mangels Vermögens abgewiesen wird, oder die Rückzahlung des Darlehens erheblich gefährdet erscheint.',
        'Mit Wirksamkeit der Kündigung wird die offene Restschuld sofort fällig.',
      ].join('\n'),
    );
  }

  if (on('aufrechnung')) {
    parts.push(
      [
        '§ 10 Aufrechnung',
        'Der Darlehensnehmer ist zur Aufrechnung nur mit rechtskräftig festgestellten oder vom Darlehensgeber anerkannten Gegenforderungen berechtigt.',
      ].join('\n'),
    );
  }

  if (on('schluss')) {
    parts.push(
      [
        '§ 11 Schriftform',
        'Änderungen und Ergänzungen dieses Vertrages bedürfen der Schriftform. Dies gilt auch für ein Abgehen von diesem Schriftformerfordernis, soweit gesetzlich zulässig.',
        '',
        '§ 12 Datenschutz',
        'Die Vertragsparteien verpflichten sich zur Einhaltung der jeweils geltenden datenschutzrechtlichen Bestimmungen.',
        '',
        '§ 13 Schlussbestimmungen',
        'Sollten einzelne Bestimmungen dieses Vertrages ganz oder teilweise unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.',
        'Auf diesen Vertrag ist ausschließlich österreichisches Recht anzuwenden.',
        'Soweit gesetzlich zulässig, wird als Gerichtsstand das sachlich zuständige Gericht am Wohn- bzw. Unternehmenssitz des Darlehensgebers vereinbart.',
        '',
        'Dieser Vertrag wird in zwei gleichlautenden Ausfertigungen errichtet.',
        `Ort: ${slot(form, 'ort')}  Datum: ${slot(form, 'datum')}`,
        `Darlehensgeber: ${slot(form, 'darlehensgeber.name')}`,
        EMPTY_SLOT,
        `Darlehensnehmer: ${slot(form, 'darlehensnehmer.name')}`,
        EMPTY_SLOT,
      ].join('\n'),
    );
  }

  return joinSections(parts);
}

function buildLizenz(ctx: Ctx): string {
  const { form, on } = ctx;
  const parts: Array<string | null> = [];

  if (on('parteien')) {
    parts.push(
      joinSections([
        'Abgeschlossen zwischen',
        partyLines(form, 'lizenzgeber', 'Lizenzgeber', [{ path: 'fn', label: 'FN' }]),
        'und',
        partyLines(form, 'lizenznehmer', 'Lizenznehmer', [{ path: 'fn', label: 'FN' }]),
        'gemeinsam als „Vertragsparteien“ bezeichnet.',
      ]),
    );
  }

  if (on('gegenstand')) {
    parts.push(
      [
        '§ 1 Vertragsgegenstand',
        `Der Lizenzgeber ist Inhaber der nachstehend bezeichneten Rechte: ${slot(form, 'gegenstand')}.`,
        'Der Lizenzgeber räumt dem Lizenznehmer die in diesem Vertrag näher bezeichneten Nutzungsrechte am Lizenzgegenstand ein.',
      ].join('\n'),
    );
  }

  if (on('gegenstand') || on('art')) {
    const art = String(getByPath(form, 'art') || '').trim();
    const exclusive = /ausschließ/i.test(art);
    parts.push(
      [
        '§ 2 Art der Lizenz',
        `Die Lizenz wird als ${art || EMPTY_SLOT} erteilt.`,
        exclusive
          ? null
          : 'Der Lizenzgeber bleibt, sofern keine ausschließliche Lizenz vereinbart wird, berechtigt, den Lizenzgegenstand selbst zu nutzen und weiteren Personen Nutzungsrechte einzuräumen.',
      ]
        .filter(Boolean)
        .join('\n'),
    );
  }

  if (on('umfang')) {
    parts.push(
      [
        '§ 3 Umfang der Nutzungsrechte',
        `Der Lizenznehmer ist berechtigt, den Lizenzgegenstand wie folgt zu nutzen: ${slot(form, 'nutzungsumfang')}.`,
        `Die Nutzung ist auf folgende Zwecke beschränkt: ${slot(form, 'zwecke')}.`,
      ].join('\n'),
    );
  }

  if (on('geltung')) {
    const gebiet = String(getByPath(form, 'gebiet') || '').trim();
    const gebietText =
      gebiet === 'sonstige' ? slot(form, 'gebiet_sonstige') : gebiet || EMPTY_SLOT;
    const zeit =
      getByPath(form, 'befristet') === true
        ? `befristet bis zum ${slot(form, 'befristung_bis')}`
        : 'auf unbestimmte Zeit';
    parts.push(
      [
        '§ 4 Räumlicher Geltungsbereich',
        `Die Lizenz gilt: ${gebietText}.`,
        '',
        '§ 5 Zeitlicher Geltungsbereich',
        `Die Lizenz wird ${zeit} eingeräumt.`,
        'Nach Vertragsende erlöschen die eingeräumten Nutzungsrechte vollständig, soweit nichts anderes vereinbart wurde.',
      ].join('\n'),
    );
  }

  if (on('gebuehr') || on('geltung')) {
    const art = String(getByPath(form, 'gebuehr_art') || '').trim();
    const detail = filled(form, 'gebuehr') ? slot(form, 'gebuehr') : EMPTY_SLOT;
    const frist = filled(form, 'zahlungsfrist_tage')
      ? slot(form, 'zahlungsfrist_tage')
      : EMPTY_SLOT;
    parts.push(
      [
        '§ 6 Lizenzgebühr',
        `Als Gegenleistung bezahlt der Lizenznehmer an den Lizenzgeber: ${art || EMPTY_SLOT} — ${detail}.`,
        `Die Zahlung ist jeweils binnen ${frist} Tagen nach Rechnungserhalt fällig.`,
      ].join('\n'),
    );
  }

  if (on('eigentum')) {
    parts.push(
      [
        '§ 7 Eigentum an Rechten',
        'Sämtliche geistigen Eigentumsrechte am Lizenzgegenstand verbleiben ausschließlich beim Lizenzgeber.',
        'Durch diesen Vertrag werden lediglich Nutzungsrechte eingeräumt. Eine Übertragung von Eigentumsrechten erfolgt nicht.',
      ].join('\n'),
    );
  }

  if (on('unterlizenz')) {
    const darf = String(getByPath(form, 'unterlizenz') || '').trim();
    parts.push(
      [
        '§ 8 Unterlizenzierung',
        `Der Lizenznehmer ist ${darf || EMPTY_SLOT}, Unterlizenzen an Dritte zu vergeben.`,
        'Eine zulässige Unterlizenzierung bedarf jedenfalls der vorherigen schriftlichen Zustimmung des Lizenzgebers.',
      ].join('\n'),
    );
  }

  if (on('gewaehrleistung')) {
    parts.push(
      [
        '§ 9 Gewährleistung',
        'Der Lizenzgeber gewährleistet, dass er nach bestem Wissen zur Einräumung der vertragsgegenständlichen Rechte berechtigt ist.',
        'Eine Gewähr für einen bestimmten wirtschaftlichen Erfolg oder eine bestimmte Verwendbarkeit des Lizenzgegenstandes wird nicht übernommen, sofern dies nicht ausdrücklich schriftlich vereinbart wurde.',
      ].join('\n'),
    );
  }

  if (on('schutzrechte')) {
    parts.push(
      [
        '§ 10 Schutzrechtsverletzungen',
        'Der Lizenznehmer hat den Lizenzgeber unverzüglich über ihm bekannt werdende Verletzungen der lizenzierten Rechte durch Dritte zu informieren.',
        'Über die Einleitung rechtlicher Schritte entscheidet grundsätzlich der Lizenzgeber.',
      ].join('\n'),
    );
  }

  if (on('vertraulichkeit')) {
    parts.push(
      [
        '§ 11 Vertraulichkeit',
        'Die Vertragsparteien verpflichten sich, sämtliche im Rahmen dieses Vertrages erlangten vertraulichen Informationen geheim zu halten.',
        'Diese Verpflichtung gilt auch nach Beendigung des Vertrages fort.',
      ].join('\n'),
    );
  }

  if (on('haftung')) {
    parts.push(
      [
        '§ 12 Haftung',
        'Die Haftung der Vertragsparteien richtet sich nach den gesetzlichen Bestimmungen.',
        'Eine Haftung für leichte Fahrlässigkeit kann, soweit gesetzlich zulässig, ausgeschlossen werden.',
        'Die Haftung für Vorsatz und Personenschäden bleibt unberührt.',
      ].join('\n'),
    );
  }

  if (on('beendigung')) {
    parts.push(
      [
        '§ 13 Vertragsbeendigung',
        'Jede Vertragspartei kann diesen Vertrag aus wichtigem Grund mit sofortiger Wirkung auflösen.',
        'Ein wichtiger Grund liegt insbesondere vor, wenn wesentliche Vertragsverletzungen trotz schriftlicher Aufforderung nicht behoben werden; eine Lizenzgebühr trotz Mahnung nicht bezahlt wird; oder ein Insolvenzverfahren eröffnet wird bzw. dessen Eröffnung mangels Vermögens abgewiesen wird.',
      ].join('\n'),
    );
  }

  if (on('loeschung')) {
    parts.push(
      [
        '§ 14 Rückgabe und Löschung',
        'Nach Beendigung des Vertrages hat der Lizenznehmer sämtliche Kopien und sonstigen Vervielfältigungen des Lizenzgegenstandes unverzüglich zu löschen bzw. zurückzugeben, sofern keine gesetzliche Aufbewahrungspflicht besteht.',
      ].join('\n'),
    );
  }

  if (on('schluss')) {
    parts.push(
      [
        '§ 15 Schlussbestimmungen',
        'Änderungen und Ergänzungen dieses Vertrages bedürfen der Schriftform.',
        'Sollten einzelne Bestimmungen dieses Vertrages unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.',
        'Auf diesen Vertrag ist ausschließlich österreichisches Recht anzuwenden.',
        '',
        '§ 16 Gerichtsstand',
        'Für sämtliche Streitigkeiten aus oder im Zusammenhang mit diesem Vertrag wird, soweit gesetzlich zulässig, die Zuständigkeit des sachlich zuständigen Gerichts am Sitz des Lizenzgebers vereinbart.',
        '',
        'Dieser Vertrag wird in zwei gleichlautenden Ausfertigungen errichtet.',
        `Ort: ${slot(form, 'ort')}  Datum: ${slot(form, 'datum')}`,
        `Lizenzgeber: ${slot(form, 'lizenzgeber.name')}`,
        EMPTY_SLOT,
        `Lizenznehmer: ${slot(form, 'lizenznehmer.name')}`,
        EMPTY_SLOT,
      ].join('\n'),
    );
  }

  return joinSections(parts);
}

function dumpGesellschafterListe(
  form: ContractValues,
  extra: Record<string, unknown>,
): string[] {
  const liste = resolveGesellschafterListe(form, extra);
  const lines: string[] = [];
  for (let i = 0; i < liste.length; i++) {
    const e = liste[i];
    const letter = gesellschafterLetter(i);
    lines.push(`  Gesellschafter ${letter}`);
    lines.push(`    name: ${(e.name || '').trim() || EMPTY_SLOT}`);
    lines.push(`    anschrift: ${(e.anschrift || '').trim() || EMPTY_SLOT}`);
    const anteil = (e.anteil_prozent || '').trim();
    lines.push(`    anteil: ${anteil || EMPTY_SLOT}`);
  }
  return lines;
}

function buildSlotDump(
  template: ContractTemplateDef,
  form: ContractValues,
  extra: Record<string, unknown>,
  on: (id: string) => boolean,
): string {
  const lines = [
    'Für diesen Typ liegt keine ARKO-Vorlage vor. Nur Pflichtfelder / leere Slots — keine erfundenen Klauseln.',
  ];
  for (const b of template.blocks) {
    if (!on(b.id)) continue;
    lines.push(`${b.label}`);
    if (template.id === 'gesellschaftervertrag' && b.id === 'gesellschafter') {
      lines.push(...dumpGesellschafterListe(form, extra));
      continue;
    }
    for (const key of b.fieldKeys) {
      const val = getByPath(form, key);
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        for (const [sk, sv] of Object.entries(val as Record<string, unknown>)) {
          const s = typeof sv === 'string' ? sv.trim() : sv == null ? '' : String(sv);
          lines.push(`  ${sk}: ${s || EMPTY_SLOT}`);
        }
      } else {
        lines.push(`  ${key}: ${slot(form, key)}`);
      }
    }
  }
  return lines.join('\n');
}

function buildGeruestBody(
  template: ContractTemplateDef,
  form: ContractValues,
  extraValues: Record<string, unknown>,
  on: (id: string) => boolean,
): string {
  const ctx: Ctx = { form, extra: extraValues || {}, on };
  switch (template.id) {
    case 'arbeitsvertrag':
      return buildArbeit(ctx);
    case 'kauf_verkauf':
      return buildKauf(ctx);
    case 'pfand_leih':
      return buildPfand(ctx);
    case 'darlehen':
      return buildDarlehen(ctx);
    case 'lizenz':
      return buildLizenz(ctx);
    default:
      return buildSlotDump(template, form, extraValues, on);
  }
}

export function buildGeruestPreview(
  template: ContractTemplateDef,
  form: ContractValues,
  enabledBlocks: string[],
  extraValues: Record<string, unknown>,
  eigeneKlauseln: EigeneKlausel[],
): string {
  const on = (id: string) => isBlockActive(template, enabledBlocks, id);
  const body = buildGeruestBody(template, form, extraValues, on);
  return joinSections([banner(template), body || 'Keine Bausteine gewählt.', eigeneBlock(eigeneKlauseln)]);
}

/** Hover-Preview eines Inhalts: dieselbe Formulierung wie im Fließtext, nur dieser Block. Kein AGB. */
export function buildGeruestBlockPreview(
  template: ContractTemplateDef,
  form: ContractValues,
  extraValues: Record<string, unknown>,
  blockId: string,
): string {
  const on = (id: string) => id === blockId;
  return buildGeruestBody(template, form, extraValues, on).trim();
}
